import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './logo.png';
import { Ico } from './shared';

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const goContact = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  };

  const links = [
    { to: '/',           label: 'Home' },
    { to: '/about',      label: 'About' },
    { to: '/services',   label: 'Services' },
    { to: '/gallery',    label: 'Gallery' },
    { to: '/industries', label: 'Industries' },
  ];

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : 'transparent'}`}>
        <div className="navbar-inner">
          <Link className="navbar-logo" to="/">
            <img src={logo} alt="Ratnam Techo Inc." />
            <div className="logo-text">
              <span className="logo-text-main">Ratnam Techo</span>
              <span className="logo-text-sub">Precision Engineering</span>
            </div>
          </Link>

          <ul className="nav-links">
            {links.map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  className={isActive(to) ? 'nav-active' : ''}
                >
                  {label}
                </Link>
              </li>
            ))}
            <li>
              <a href="/#contact" onClick={goContact} className="nav-cta">
                Get a Quote
              </a>
            </li>
          </ul>

          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <ul>
          {links.map(({ to, label }) => (
            <li key={to}>
              <Link
                to={to}
                className={isActive(to) ? 'mobile-nav-active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </Link>
            </li>
          ))}
          <li>
            <a href="/#contact" onClick={goContact} className="mobile-cta-link">
              Get a Free Quote
            </a>
          </li>
        </ul>
      </div>
    </>
  );
}

export function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goContact = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-logo">
              <img src={logo} alt="Ratnam Techo Inc." />
              <div className="logo-text">
                <span className="logo-text-main">Ratnam Techo</span>
                <span className="logo-text-sub">Precision Engineering</span>
              </div>
            </div>
            <p className="footer-tagline">
              Precision engineering, custom fabrication, and glass burner solutions
              for professionals who demand the best.
            </p>
          </div>

          <div>
            <div className="footer-heading">Quick Links</div>
            <ul className="footer-links">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/industries">Industries</Link></li>
              <li><a href="/#contact" onClick={goContact}>Contact</a></li>
            </ul>
          </div>

          <div>
            <div className="footer-heading">Contact</div>
            <div className="footer-contact-item">
              <span className="footer-contact-icon"><Ico name="whatsapp" size={15} /></span>
              +1 (416) 417-2091
            </div>
            <div className="footer-contact-item">
              <span className="footer-contact-icon"><Ico name="mail" size={15} /></span>
              info@ratnamtecho.com
            </div>
            <div className="footer-contact-item">
              <span className="footer-contact-icon"><Ico name="mappin" size={15} /></span>
              Ontario, Canada
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">
            © 2025 <span>Ratnam Techo Inc.</span> All rights reserved.
          </p>
          <p className="footer-copy">
            Precision Engineering · Custom Fabrication · Glass Torches
          </p>
        </div>
      </div>
    </footer>
  );
}

export function PageHeader({ title, highlight, sub, crumb }) {
  return (
    <section className="page-header">
      <div className="page-header-bg" />
      <div className="page-header-grid" />
      <div className="page-header-content">
        <div className="breadcrumb-bar">
          <Link to="/">Home</Link>
          <span className="bc-sep">/</span>
          <span>{crumb || title}</span>
        </div>
        <h1>
          {title}{highlight && <><br /><span className="gold">{highlight}</span></>}
        </h1>
        {sub && <p className="page-header-sub">{sub}</p>}
      </div>
    </section>
  );
}

export function SectionCTA({ heading = "Ready to Start Your Project?", sub }) {
  const navigate = useNavigate();
  const location = useLocation();

  const goContact = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  };

  return (
    <section className="section-cta-band">
      <div className="section-cta-inner">
        <h2>{heading}</h2>
        <p>{sub || "Contact us today for a free quote tailored to your project requirements."}</p>
        <div className="section-cta-btns">
          <a
            href="https://wa.me/14164172091"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary cta-btn cta-btn-whatsapp"
          >
            <Ico name="whatsapp" size={20} />
            Chat on WhatsApp
          </a>
          <a href="/#contact" onClick={goContact} className="btn-outline" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
            Get a Free Quote
          </a>
        </div>
      </div>
    </section>
  );
}
